// Top-of-page banner on My Invoices when we can't charge the client's card:
// declined, expired, expiring, or nothing on file. Reads the stored verdict
// on each billing profile (refreshed nightly and on every Stripe payment
// event), so it costs no Stripe call.
import React from 'react';

import { Alert, Button, Typography } from '@mui/material';

const COPY = {
  failing: {
    severity: 'error',
    title: 'Your card on file was declined',
    body: (h) =>
      `${h.method_label ? `${h.method_label}: ` : ''}${
        h.last_failure_message || 'the bank refused the last charge.'
      } Add a new card and anything past due is charged to it right away.`,
  },
  expired: {
    severity: 'error',
    title: 'Your card on file has expired',
    body: (h) =>
      `${h.method_label || 'Your card'} has expired. Your agreement keeps a working card on file — please add a new one.`,
  },
  missing: {
    severity: 'warning',
    title: 'No card on file',
    body: () =>
      'Your agreement keeps a working card on file as the backup for your invoices. It takes a minute to add one.',
  },
  expiring: {
    severity: 'warning',
    title: 'Your card expires soon',
    body: (h) =>
      `${h.method_label || 'Your card'} expires ${
        h.card_expires_on ? new Date(`${h.card_expires_on}T00:00:00`).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) : 'soon'
      }. Add the new card so billing isn't interrupted.`,
  },
};

const ORDER = ['failing', 'expired', 'missing', 'expiring'];

export default function PaymentHealthBanner({ profiles, onUpdate }) {
  const healths = (profiles || []).map((p) => p.payment_health).filter(Boolean);
  const worst = ORDER.find((s) => healths.some((h) => h.status === s));
  if (!worst) return null;
  const health = healths.find((h) => h.status === worst);
  const copy = COPY[worst];
  return (
    <Alert
      severity={copy.severity}
      sx={{ borderRadius: 2 }}
      action={
        <Button color="inherit" variant="outlined" size="small" onClick={onUpdate}>
          Update card
        </Button>
      }
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {copy.title}
      </Typography>
      <Typography variant="body2">{copy.body(health)}</Typography>
      {health.budget_gate_enabled && health.blocking && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          New budgets go live as soon as a working card is saved.
        </Typography>
      )}
    </Alert>
  );
}
